import type { IFetchPathAnalysisProps } from './useFetchPathAnalysis';

import { getCacheParameterKey, getPathAnalyticMetricQueryParams } from '~/modules/analytics';
import { SHOPIFY_PAGE_ID_KEY, LOCATION_PATH_KEY } from '~/modules/page-analytic';
import { EAnalyticMode } from '~/modules/analytics/types';
import { useFeatureFlagChecker } from '~/modules/apps';
import { useSearchParams } from '@remix-run/react';
import { usePathAnalysisStore } from '../stores';
import { useMemo } from 'react';

export const usePathAnalysisMetricParameter = (props: IFetchPathAnalysisProps) => {
  const [searchParams] = useSearchParams();

  const { isEnabledFilterByShopifyPageId } = useFeatureFlagChecker();

  const firstLevelLocationPath = usePathAnalysisStore((state) => state.firstLevelLocationPath);

  const shopifyPageID = searchParams.get(SHOPIFY_PAGE_ID_KEY) || '';
  const locationPath = searchParams.get(LOCATION_PATH_KEY) || firstLevelLocationPath || '';

  const baseParameter = useMemo(() => {
    return {
      mode: props.mode || EAnalyticMode.ALL_SESSION,
      shopifyPageID,
      campaignVariantID: props.campaignVariantID,
      campaignVersionID: props.campaignVersionID,
      locationPath,
      isEnabledFilterByShopifyPageId,
    };
  }, [props, shopifyPageID, locationPath, isEnabledFilterByShopifyPageId]);

  const currentParameters = useMemo(() => {
    return getPathAnalyticMetricQueryParams({ ...baseParameter, dateRange: props.currentDates });
  }, [baseParameter, props.currentDates]);

  const previousParameters = useMemo(() => {
    return getPathAnalyticMetricQueryParams({ ...baseParameter, dateRange: props.previousDates });
  }, [baseParameter, props.previousDates]);

  const isEnabledQuery = useMemo(() => !!currentParameters.input.mode, [currentParameters]);

  return {
    parameterKey: getCacheParameterKey(currentParameters),
    previousParameterKey: getCacheParameterKey(previousParameters),
    isEnabledQuery,
    parameter: currentParameters,
    previousParameter: previousParameters,
    locationPath,
  };
};
